import { Alert, AlertIcon, AlertDescription, Box, Flex, Icon, Text, Heading, Tooltip } from '@chakra-ui/react';
import { ListingModel } from 'models';
import { AppConfig } from 'config/app';
import { MdAttachMoney } from 'react-icons/md';
import { FaRegClone } from 'react-icons/fa';
import { GiMaterialsScience } from 'react-icons/gi';
import { IconType } from 'react-icons';

interface Warning {
  message: string;
  icon: IconType;
}

interface WarningData {
  count: number;
  warnings: Warning[];
}

interface OriginalMarkingWarningProps {
  listings: ListingModel[];
  suggestionType: 'ORIGINAL' | 'MULTIPLE_ITEMS' | 'FAKE' | 'REJECT' | 'SET';
}

export default function OriginalMarkingWarning({
  listings,
  suggestionType
}: OriginalMarkingWarningProps) {
  // Only show warnings for ORIGINAL marking
  if (suggestionType !== 'ORIGINAL') {
    return null;
  }

  const getWarningData = (): WarningData => {
    const warningCounts = {
      hasEdition: 0,
      lowPrice: 0,
      hasPrintMaterial: 0
    };

    listings.forEach(listing => {
      if (listing.hasEdition()) {
        warningCounts.hasEdition++;
      }
      if (listing.rawPrice < AppConfig.ORIGINAL_MARKING_MIN_PRICE_THRESHOLD) {
        warningCounts.lowPrice++;
      }
      if (listing.material && listing.material.toLowerCase().includes('print')) {
        warningCounts.hasPrintMaterial++;
      }
    });

    const warnings: Warning[] = [];
    if (warningCounts.hasEdition > 0) {
      const suffix = listings.length > 1 ? ` listing(s) have` : ` listing has`;
      warnings.push({
        message: `${warningCounts.hasEdition}${suffix} an edition size`,
        icon: FaRegClone
      });
    }
    if (warningCounts.lowPrice > 0) {
      const suffix = listings.length > 1 ? ` listing(s) priced` : ` listing priced`;
      warnings.push({
        message: `${warningCounts.lowPrice}${suffix} below $${AppConfig.ORIGINAL_MARKING_MIN_PRICE_THRESHOLD}`,
        icon: MdAttachMoney
      });
    }
    if (warningCounts.hasPrintMaterial > 0) {
      const suffix = listings.length > 1 ? ` listing(s) have` : ` listing has`;
      warnings.push({
        message: `${warningCounts.hasPrintMaterial}${suffix} material containing "print"`,
        icon: GiMaterialsScience
      });
    }

    const totalProblematicListings = new Set(
      listings.filter(listing =>
        listing.hasEdition() ||
        listing.rawPrice < AppConfig.ORIGINAL_MARKING_MIN_PRICE_THRESHOLD ||
        (listing.material && listing.material.toLowerCase().includes('print'))
      )
    ).size;

    return { count: totalProblematicListings, warnings };
  };

  const warningData = getWarningData();
  const hasWarnings = warningData.warnings.length > 0;

  if (!hasWarnings) {
    return null;
  }

  const countMessage = listings.length >= 1
    ? ` ${warningData.count} of ${listings.length} selected listing(s) may not be original (non-editioned).`
    : '';

  const tooltipCopy =
    'Original items are unique (non-editioned) and generally done on acrylic or paper and not printed. They are also generally the most expensive pieces available for an artist.';

  return (
    <Alert status="warning" borderRadius="md">
      <AlertDescription fontSize={'sm'} width="100%">
        <Box>
          <Flex align="center" mb={3} wrap="wrap">
            <AlertIcon boxSize={6} color="orange.700" />
            <Tooltip label={tooltipCopy} hasArrow placement="top" maxW="360px">
              <Heading size="xs" color="orange.700" cursor="help">
                Verify Carefully
              </Heading>
              <Text fontSize={'xs'}>
                {countMessage}
              </Text>
            </Tooltip>
          </Flex>
          <Box marginLeft={8}>
            {warningData.warnings.map((warning, index) => (
              <Flex key={index} align="center" gap={2} mb={1}>
                <Icon as={warning.icon} w={4} h={4} color="orange.600" />
                <Text fontSize="xs">{warning.message}</Text>
              </Flex>
            ))}
          </Box>
        </Box>
      </AlertDescription>
    </Alert>
  );
}
